import { useEffect } from 'react';

const SITE_ORIGIN = 'https://eaaproof.com';
const DEFAULT_SUFFIX = 'EAA Proof';
const ROBOTS_NAME = 'robots';
const CANONICAL_REL = 'canonical';
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-card.png`;

export interface HreflangAlternate {
  /** BCP 47 language tag (e.g. "en", "de", "fr", or with region "de-AT"). */
  lang: string;
  /** Absolute or root-relative URL of the localized variant. */
  href: string;
}

export interface SeoOptions {
  /** Page-specific title prefix. " — EAA Proof" is appended automatically unless includeSuffix=false. */
  title: string;
  /** Path on this site (e.g. "/pricing") used for the canonical URL. Defaults to current location. */
  canonicalPath?: string;
  /** Set true on auth-gated / private routes so they don't get indexed. */
  noindex?: boolean;
  /** Skip the " — EAA Proof" suffix if your title already contains the brand. */
  includeSuffix?: boolean;
  /** Plain-text page description. Drives <meta name="description">, og:description, twitter:description. */
  description?: string;
  /** Absolute URL of the share image. Defaults to /og-card.png. Set per-page for richer link previews. */
  ogImage?: string;
  /**
   * Localized variants of this page. Adds <link rel="alternate" hreflang="…"> tags so Google
   * serves the right language in each EU market. ALWAYS include x-default for the language
   * an unmatched visitor lands on (usually English).
   *
   * Example for the EAA compliance landing page targeted in EN + DE + FR ads:
   *   alternates: [
   *     { lang: 'en',        href: 'https://eaaproof.com/lp/eaa-compliance' },
   *     { lang: 'de',        href: 'https://eaaproof.com/de/lp/eaa-compliance' },
   *     { lang: 'fr',        href: 'https://eaaproof.com/fr/lp/eaa-compliance' },
   *     { lang: 'x-default', href: 'https://eaaproof.com/lp/eaa-compliance' },
   *   ]
   */
  alternates?: HreflangAlternate[];
}

/**
 * Per-route SEO controls for an SPA. Without server-side rendering we can't ship the right
 * tags in the initial HTML, but we can still set them in the live DOM so crawlers that DO
 * execute JS (Googlebot, Bingbot, modern Slackbot) read them correctly. AI search bots that
 * don't execute JS will see the static defaults from index.html.
 *
 *   - title: replaces document.title for the component's lifetime, restored on unmount.
 *   - canonicalPath: ensures <link rel="canonical"> points at the right URL per route.
 *   - noindex: adds <meta name="robots" content="noindex,nofollow"> while mounted.
 *   - description / ogImage: drives <meta name="description"> + Open Graph + Twitter Card
 *     so link previews on LinkedIn/Slack/Discord/X render with the page-specific copy.
 */
export function useSeo(options: SeoOptions): void {
  const {
    title,
    canonicalPath,
    noindex = false,
    includeSuffix = true,
    description,
    ogImage,
    alternates,
  } = options;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = includeSuffix ? `${title} — ${DEFAULT_SUFFIX}` : title;

    const canonical = upsertLink(CANONICAL_REL);
    const previousCanonical = canonical.getAttribute('href');
    const canonicalHref = canonicalPath
      ? `${SITE_ORIGIN}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`
      : `${SITE_ORIGIN}${window.location.pathname}`;
    canonical.setAttribute('href', canonicalHref);

    let robotsMeta: HTMLMetaElement | null = null;
    let robotsCreated = false;
    let previousRobots: string | null = null;
    if (noindex) {
      const existing = document.head.querySelector<HTMLMetaElement>(`meta[name="${ROBOTS_NAME}"]`);
      if (existing) {
        robotsMeta = existing;
        previousRobots = existing.getAttribute('content');
      } else {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = ROBOTS_NAME;
        document.head.appendChild(robotsMeta);
        robotsCreated = true;
      }
      robotsMeta.setAttribute('content', 'noindex,nofollow');
    }

    // Title-mirroring tags. og:title and twitter:title get the displayed title (with suffix)
    // because that's what people see in the link preview card.
    const displayedTitle = document.title;
    const metaCleanup: Array<() => void> = [];
    metaCleanup.push(setMeta('property', 'og:title', displayedTitle));
    metaCleanup.push(setMeta('name', 'twitter:title', displayedTitle));
    metaCleanup.push(setMeta('property', 'og:url', canonicalHref));
    metaCleanup.push(setMeta('property', 'og:type', 'website'));
    metaCleanup.push(setMeta('property', 'og:site_name', DEFAULT_SUFFIX));
    metaCleanup.push(setMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary'));

    if (description) {
      metaCleanup.push(setMeta('name', 'description', description));
      metaCleanup.push(setMeta('property', 'og:description', description));
      metaCleanup.push(setMeta('name', 'twitter:description', description));
    }

    const effectiveOgImage = ogImage ?? DEFAULT_OG_IMAGE;
    metaCleanup.push(setMeta('property', 'og:image', effectiveOgImage));
    metaCleanup.push(setMeta('name', 'twitter:image', effectiveOgImage));

    // hreflang alternates. We track every <link rel="alternate" hreflang="..."> we add so we
    // can remove the exact tags on unmount — never touch tags that already existed in static
    // HTML, which would risk wiping a future build-time injected set.
    const alternateNodes: HTMLLinkElement[] = [];
    if (alternates && alternates.length > 0) {
      for (const alt of alternates) {
        const link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = alt.lang;
        link.href = alt.href.startsWith('http') ? alt.href : `${SITE_ORIGIN}${alt.href}`;
        link.dataset.useSeo = 'alternate';
        document.head.appendChild(link);
        alternateNodes.push(link);
      }
    }

    return () => {
      document.title = previousTitle;
      if (previousCanonical !== null) {
        canonical.setAttribute('href', previousCanonical);
      }
      if (robotsMeta) {
        if (robotsCreated) {
          robotsMeta.remove();
        } else if (previousRobots !== null) {
          robotsMeta.setAttribute('content', previousRobots);
        } else {
          robotsMeta.removeAttribute('content');
        }
      }
      metaCleanup.forEach(undo => undo());
      alternateNodes.forEach(node => node.remove());
    };
  }, [title, canonicalPath, noindex, includeSuffix, description, ogImage, alternates]);
}

function upsertLink(rel: string): HTMLLinkElement {
  const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (existing) return existing;
  const link = document.createElement('link');
  link.rel = rel;
  document.head.appendChild(link);
  return link;
}

/**
 * Upserts a <meta> tag and returns an undo function that restores the previous state.
 * Handles the two flavors used in this codebase: name=... (HTML standard, Twitter) and
 * property=... (Open Graph). The same logical key (e.g. "description") may appear under
 * both spellings; we treat each independently so callers can set whichever they need.
 */
function setMeta(attrKind: 'name' | 'property', key: string, value: string): () => void {
  const selector = `meta[${attrKind}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) {
    const previous = existing.getAttribute('content');
    existing.setAttribute('content', value);
    return () => {
      if (previous !== null) existing.setAttribute('content', previous);
      else existing.removeAttribute('content');
    };
  }
  const meta = document.createElement('meta');
  meta.setAttribute(attrKind, key);
  meta.setAttribute('content', value);
  document.head.appendChild(meta);
  return () => { meta.remove(); };
}
